import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * Mirrors the Supabase Auth password policy on every project:
 * min length 8, with a lowercase letter, an uppercase letter, a digit and a symbol.
 * Keep in sync with password_min_length / password_required_characters.
 */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * A password as it is saved and signed in with: without the spaces a paste or a
 * stray keypress leaves at either end. Spaces inside it are kept.
 */
export function normalizePassword(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

export interface PasswordRules {
  minLength: boolean;
  lower: boolean;
  upper: boolean;
  number: boolean;
  special: boolean;
}

export function evaluatePassword(value: string | null | undefined): PasswordRules {
  const v = normalizePassword(value);
  return {
    minLength: v.length >= PASSWORD_MIN_LENGTH,
    lower: /[a-z]/.test(v),
    upper: /[A-Z]/.test(v),
    number: /\d/.test(v),
    special: /[^A-Za-z0-9]/.test(v),
  };
}

export type PasswordStrength = 'weak' | 'normal' | 'strong';

/** Weak until every policy rule is met, normal once it is, strong from 12 characters up. */
export const PASSWORD_STRONG_LENGTH = 12;

export function passwordStrength(value: string | null | undefined): PasswordStrength {
  const v = normalizePassword(value);
  if (!Object.values(evaluatePassword(v)).every(Boolean)) return 'weak';
  return v.length >= PASSWORD_STRONG_LENGTH ? 'strong' : 'normal';
}

export function passwordPolicyValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '');
  if (!value) return null; // `required` reports empty values (all spaces fails minLength below)
  return Object.values(evaluatePassword(value)).every(Boolean) ? null : { passwordPolicy: true };
}
